import React, { useEffect, useState, useRef } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./LiveFormPreview.css";
import { Editor } from "@tinymce/tinymce-react";
import { el } from "date-fns/locale";

const LAYOUT_STORAGE_KEY = "formLayouts";

const LiveFormPreview = ({
  customFields,
  fieldValues,
  setFieldValues,
  setCustomFields,
  layouts,
  setLayouts,
  handleRemoveField,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [targetGroup, setTargetGroup] = useState(null);
  const blurTimeoutRef = useRef(null);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Handle drag start between groups
  const handleDragStart = (e) => {
    const element = e.currentTarget;
    const groupIndex = parseInt(element.getAttribute("data-group-index"));
    const fieldId = element.getAttribute("data-field-id");
    const fieldElement = element.closest(".react-grid-item > div");
    if (fieldElement) {
      e.dataTransfer.setDragImage(fieldElement, 0, 0);
    }
    setDraggedItem({
      groupIndex,
      fieldId,
      element,
    });

    e.dataTransfer.setData("text/plain", `${groupIndex}-${fieldId}`);
    e.dataTransfer.effectAllowed = "move";
  };

  // Handle drag over a potential target group
  const handleDragOver = (e, groupIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (draggedItem && draggedItem.groupIndex !== groupIndex) {
      setTargetGroup(groupIndex);
    }
  };

  // Handle drop to move item to new group
  const handleDrop = (e, targetGroupIndex) => {
    e.preventDefault();

    // Only handle cross-group drops
    if (!draggedItem || draggedItem.groupIndex === targetGroupIndex) {
      setDraggedItem(null);
      setTargetGroup(null);
      return;
    }

    const sourceGroupIndex = draggedItem.groupIndex;
    const fieldId = draggedItem.fieldId;

    const sourceGroup = [...customFields[sourceGroupIndex]];
    const fieldIndex = sourceGroup.findIndex(
      (f) => f.id.toString() === fieldId.toString()
    );

    if (fieldIndex === -1) {
      setDraggedItem(null);
      setTargetGroup(null);
      return;
    }

    const fieldToMove = sourceGroup[fieldIndex];

    setCustomFields((prev) => {
      const newFields = [...prev];
      newFields[sourceGroupIndex] = newFields[sourceGroupIndex].filter(
        (f) => f.id.toString() !== fieldId.toString()
      );

      if (!newFields[targetGroupIndex]) {
        newFields[targetGroupIndex] = [];
      }
      newFields[targetGroupIndex] = [
        ...newFields[targetGroupIndex],
        fieldToMove,
      ];

      return newFields.filter((group) => group.length > 0);
    });

    setLayouts((prev) => {
      const newLayouts = { ...prev };

      // Remove from source group
      if (newLayouts[sourceGroupIndex]) {
        newLayouts[sourceGroupIndex] = newLayouts[sourceGroupIndex].filter(
          (l) => !l.i.startsWith(`${sourceGroupIndex}-${fieldId}`)
        );
      }

      return newLayouts;
    });

    setDraggedItem(null);
    setTargetGroup(null);
  };

  // Handle drag end (cleanup)
  const handleDragEnd = () => {
    setDraggedItem(null);
    setTargetGroup(null);
  };

  useEffect(() => {
    const initialLayouts = { ...layouts };
    let needsUpdate = false;

    customFields.forEach((group, groupIndex) => {
      if (!initialLayouts[groupIndex]) {
        initialLayouts[groupIndex] = [];
      }

      group.forEach((field, fieldIndex) => {
        const fieldKey = `${groupIndex}-${field.id}`;
        const existingLayout = initialLayouts[groupIndex].find(
          (l) => l.i === fieldKey
        );

        if (!existingLayout) {
          const existingItems = initialLayouts[groupIndex];
          let maxY = -1;
          let maxXInMaxY = 0;

          for (const item of existingItems) {
            const itemEndX = item.x + item.w;
            if (item.y > maxY) {
              maxY = item.y;
              maxXInMaxY = itemEndX;
            } else if (item.y === maxY) {
              if (itemEndX > maxXInMaxY) {
                maxXInMaxY = itemEndX;
              }
            }
          }

          let x = 0;
          let y = 0;
          let w = field.type === "textEditor" ? 10 : 4;
          let h = field.type === "textEditor" ? 4 : 1;

          if (maxY === -1) {
            x = 0;
            y = 0;
          } else {
            if (maxXInMaxY + w > 12) {
              x = 0;
              y = maxY + 1;
            } else {
              x = maxXInMaxY;
              y = maxY;
            }
          }

          initialLayouts[groupIndex].push({
            i: fieldKey,
            x,
            y,
            w: Math.min(w, 12),
            h,
          });
          needsUpdate = true;
        }
      });

      const currentFieldIds = group.map((f) => `${groupIndex}-${f.id}`);
      initialLayouts[groupIndex] = initialLayouts[groupIndex].filter((l) =>
        currentFieldIds.includes(l.i)
      );
    });

    if (needsUpdate) {
      setLayouts(initialLayouts);
    }
  }, [customFields, layouts, setLayouts]);

  // Save layouts to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layouts));
  }, [layouts]);

  useEffect(() => {
    const handleMouseDown = (e) => {
      if (e.target.closest(".ql-toolbar button, .ql-toolbar .ql-picker-item")) {
        setIsEditing(true);
        e.stopPropagation();
      }
    };

    document.addEventListener("mousedown", handleMouseDown, true);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown, true);
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const handleLayoutChange = (newLayout, groupIndex) => {
    // Only update if the layout actually changed
    const currentLayout = layouts[groupIndex] || [];
    if (JSON.stringify(currentLayout) === JSON.stringify(newLayout)) return;

    const constrainedLayout = newLayout.map((item) => ({
      ...item,
      w: Math.min(item.w, 12),
    }));

    const updatedLayouts = {
      ...layouts,
      [groupIndex]: constrainedLayout,
    };

    setLayouts(updatedLayouts);

    // Update customFields with layout positions
    setCustomFields((prevFields) => {
      return prevFields.map((group, gIndex) => {
        if (gIndex !== groupIndex) return group;

        return group.map((field) => {
          const layoutItem = constrainedLayout.find(
            (l) => l.i === `${groupIndex}-${field.id}`
          );
          if (!layoutItem) return field;

          return {
            ...field,
            layout: {
              x: layoutItem.x,
              y: layoutItem.y,
              w: layoutItem.w,
              h: layoutItem.h,
            },
          };
        });
      });
    });
  };

  // ... (rest of your component code remains the same)
  const handleFieldChange = (field, e, subfield = null) => {
    let value;
    const currentValue = fieldValues[field.id] || {};

    if (field.type === "checkbox") {
      const checked = e.target.checked;
      const optionValue = e.target.value;
      const prev = field.value || [];
      value = checked
        ? [...prev, optionValue]
        : prev.filter((v) => v !== optionValue);
    } else if (field.type === "file") {
      value = e.target.files[0];
    } else if (field.type === "heading") {
      value = {
        ...currentValue,
        [subfield]: e.target.value,
      };
    } else {
      value = e.target.value;
    }

    setFieldValues((prev) => ({
      ...prev,
      [field.id]: value,
    }));

    const updatedFields = customFields.map((group) =>
      group.map((f) => (f.id === field.id ? { ...f, value } : f))
    );

    setCustomFields(updatedFields);
  };

  const handleRemove = (fieldId) => {
    const groupIndexToUpdate = customFields.findIndex((group) =>
      group.some((field) => field.id === fieldId)
    );

    if (groupIndexToUpdate === -1) return;

    const updatedCustomFields = customFields
      .map((group, index) => {
        if (index === groupIndexToUpdate) {
          return group.filter((field) => field.id !== fieldId);
        }
        return group;
      })
      .filter((group) => group.length > 0);

    setCustomFields(updatedCustomFields);

    setFieldValues((prev) => {
      const newValues = { ...prev };
      delete newValues[fieldId];
      return newValues;
    });
  };

  const renderField = (field, groupIndex) => {
    const value = fieldValues[field.id] ?? "";

    const labelStyle =
      "text-sm whitespace-nowrap text-black font-medium mr-[5px]";
    const baseLabelStyle = "text-sm mr-2";
    const boldStyle = field.boldText ? "font-bold" : "font-normal";
    const requiredStyle = field.mandatory ? (
      <span className="text-red-500 ml-1">*</span>
    ) : null;
    const inputStyle = "flex-1 border px-3 py-2 bg-gray-200";

    switch (field.type) {
      case "text":
        return (
          <div className={`flex items-center ${field.styles?.container || ""}`}>
            <label className={`${baseLabelStyle} ${boldStyle}`}>
              {field.label}
              {requiredStyle}
            </label>
          </div>
        );
      case "textarea":
        return (
          <div
            className={`flex flex-col ${baseLabelStyle} ${
              field.styles?.container || ""
            }`}
          >
            <label className={`${baseLabelStyle} mb-1`}>
              {field.label}
              {field.mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              className="border border-gray-300 rounded-md shadow-sm p-2"
              placeholder={field.placeholder || ""}
              value={value}
              onChange={(e) => handleFieldChange(field, e)}
              ref={(el) => {
                  if (el) {
                  el.style.height = 'auto';
                  el.style.height = `${el.scrollHeight}px`;
                  }
              }}
              onInput={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
              }}
            />
          </div>
        );
      case "input":
        return (
          <div className={`${field.styles.container}`}>
            <label className={`${baseLabelStyle} mb-1`}>
              {field.label}
              {field.mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              className={inputStyle}
              placeholder={field.placeholder || ""}
              value={value}
              onChange={(e) => handleFieldChange(field, e)}
            />
          </div>
        );
      case "textEditor": {
        return (
          <div className="flex flex-col gap-1 w-full">
            <label className="text-sm">
              {field.label}
              {field.mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="border rounded overflow-hidden">
              <Editor
                apiKey={process.env.REACT_APP_TINY_MCE_KEY}
                value={value}
                onEditorChange={(content) => {
                  handleFieldChange(field, { target: { value: content } });
                }}
                init={{
                  menubar: "file edit view insert format tools table help",
                  plugins: [
                    "lists","advlist autolink  link image charmap preview anchor",
                    "searchreplace visualblocks code fullscreen",
                    "insertdatetime media table paste help wordcount",
                    "autoresize",
                    "table",
                  ],
                  toolbar:
                    "undo redo | formatselect | " +
                    "bold italic underline strikethrough blockquote | " +
                    "alignleft aligncenter alignright alignjustify | " +
                    "outdent indent | " +
                    "forecolor backcolor | fontselect fontsizeselect | " +
                    "link image media | table tabledelete | removeformat | help" + " bullist numlist",
                  autoresize_bottom_margin: 10,
                  autoresize_min_height: 150,
                  autoresize_overflow_padding: 5,
                  content_style:
                    "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                }}
                onFocus={() => setIsEditing(true)}
                onBlur={() => setIsEditing(false)}
              />
            </div>
          </div>
        );
      }
      case "select":
        return (
          <div
            className={`select-container flex items-center gap-4 ${baseLabelStyle} ${
              field.styles?.container || ""
            }`}
          >
            <label className={`${baseLabelStyle} mb-1`}>
              {field.label}
              {field.mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className=" flex-1 min-w-[150px]">
              <select
                className={`${inputStyle} w-full appearance-none pr-8`}
                value={value}
                onChange={(e) => handleFieldChange(field, e)}
              >
                <option value="">Select {field.label.toLowerCase()}</option>
                {field.options?.map((opt, idx) => (
                  <option key={idx} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
        );
      case "radio":
        return (
          <div
            className={`flex items-start gap-2 ${
              field.styles?.optionsContainer || ""
            }`}
          >
            <label className={`${baseLabelStyle} mb-1`}>
              {field.label}
              {field.mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="flex flex-wrap gap-4">
              {field.options?.map((opt, idx) => (
                <label
                  key={idx}
                  className="inline-flex items-center gap-1 max-w-full"
                >
                  <input
                    type="radio"
                    name={`radio-${field.id}`}
                    value={opt}
                    checked={value === opt}
                    onChange={(e) => handleFieldChange(field, e)}
                    className="non-draggable flex-shrink-0"
                  />
                  <span className="whitespace-normal break-words">{opt}</span>
                </label>
              ))}
            </div>
          </div>
        );
      case "checkbox":
        return (
          <div
            className={`flex items-start gap-2 ${
              field.styles?.optionsContainer || ""
            }`}
          >
            <label className={`${baseLabelStyle} mb-1`}>
              {field.label}
              {field.mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className={`flex gap-4`}>
              {field.options?.map((opt, idx) => (
                <label
                  key={idx}
                  className="flex items-center justify-center gap-1"
                >
                  <input
                    type="checkbox"
                    value={opt}
                    checked={(value || []).includes(opt)}
                    onChange={(e) => handleFieldChange(field, e)}
                    className="ml-1 non-draggable"
                  />
                  <span className="whitespace-normal break-words">{opt}</span>
                </label>
              ))}
            </div>
          </div>
        );
      case "date":
        return (
          <div className={`${field.styles.container}`}>
            <label className={`${baseLabelStyle} mb-1`}>
              {field.label}
              {field.mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="date"
              className={`${inputStyle || ""} non-draggable`}
              value={value}
              onChange={(e) => handleFieldChange(field, e)}
            />
          </div>
        );
      case "file":
        return (
          <div className="flex flex-col">
            <label className={`${baseLabelStyle} mb-1`}>
              {field.label}
              {field.mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            {value ? (
              <img
                src={
                  typeof value === "string" ? value : URL.createObjectURL(value)
                }
                alt="Uploaded"
                className="max-w-xs h-auto rounded border"
              />
            ) : (
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.svg,.jfif"
                onChange={(e) => handleFieldChange(field, e)}
                className="text-xs"
              />
            )}
          </div>
        );
      case "html":
        return <div className="inner-html" dangerouslySetInnerHTML={{ __html: field.label }} />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-[#F9F9F9] overflow-x-auto">
      <div className="bg-white">
        <div className="flex justify-center">
          <div ref={containerRef} className="w-full">
            {customFields.map((group, groupIndex) => (
              <div
                key={groupIndex}
                className="relative"
                onDragOver={(e) => handleDragOver(e, groupIndex)}
                onDrop={(e) => handleDrop(e, groupIndex)}
                style={{
                  border:
                    targetGroup === groupIndex ? "1px dashed #3182ce" : "",
                  backgroundColor: targetGroup === groupIndex ? "#ebf8ff" : "",
                }}
              >
                <GridLayout
                  className="layout"
                  cols={12}
                  rowHeight={30}
                  margin={[10, 10]}
                  containerPadding={[0, 10]}
                  style={{ fontSize: "12px" }}
                  width={containerWidth}
                  layout={layouts[groupIndex] || []}
                  isDraggable={!isEditing}
                  isResizable={!isEditing}
                  draggableCancel=".non-draggable, .ql-toolbar, .ql-container, .cross-group-drag-handle"
                  onLayoutChange={(newLayout) =>
                    handleLayoutChange(newLayout, groupIndex)
                  }
                  isBounded={false}
                  compactType={null}
                  preventCollision={false}
                  useCSSTransforms={true}
                  autoSize={true}
                >
                  {group.map((field) => {
                    const key = `${groupIndex}-${field.id}`;
                    const layoutItem = layouts[groupIndex]?.find(
                      (l) => l.i === key
                    ) || {
                      i: key,
                      x: 0,
                      y: 0,
                      w: field.type === "textEditor" ? 10 : 4,
                      h: field.type === "textEditor" ? 6 : 1,
                    };

                    const safeLayout = {
                      i: key,
                      x: Number.isFinite(layoutItem.x) ? layoutItem.x : 0,
                      y: Number.isFinite(layoutItem.y) ? layoutItem.y : 0,
                      w: Math.min(
                        Number.isFinite(layoutItem.w)
                          ? layoutItem.w
                          : field.type === "textEditor"
                          ? 10
                          : 4,
                        12
                      ),
                      h: Number.isFinite(layoutItem.h)
                        ? layoutItem.h
                        : field.type === "textEditor"
                        ? 6
                        : 1,
                    };

                    return (
                      <div
                        key={key}
                        data-grid={safeLayout}
                        style={{ zIndex: 100 }}
                      >
                        <div className="mb-1 bg-white">
                          <div className="flex justify-start gap-1 items-start">
                            <div
                              className="cross-group-drag-handle cursor-grab"
                              draggable
                              onDragStart={handleDragStart}
                              onDragEnd={handleDragEnd}
                              data-group-index={groupIndex}
                              data-field-id={field.id}
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              ⠿
                            </div>
                            <div className="w-full">{renderField(field, groupIndex)}</div>
                            <button
                              type="button"
                              onClick={() => handleRemove(field.id)}
                              className=" z-50 non-draggable bg-red-600 hover:bg-red-800 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full buttoncross"
                            >
                              x
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </GridLayout>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveFormPreview;
